import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from './utils/apiConfig';
import GroupConfigurationPanel from './components/GroupConfigurationPanel';
import TeamPickingInterface from './components/TeamPickingInterface';
import TeamSelectionModal from './components/TeamSelectionModal';
import './styles-tournament-grouping.css';

function buildLogoUrl(logo) {
  if (!logo) return '';
  if (logo.startsWith('http') || logo.startsWith('blob:') || logo.startsWith('data:')) return logo;
  const path = logo.startsWith('/') ? logo.slice(1) : logo;
  return path.startsWith('uploads') ? `${API_BASE_URL}/${path}` : `${API_BASE_URL}/${path}`;
}

function TournamentGrouping() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [pickingState, setPickingState] = useState(null);
  const [showConfiguration, setShowConfiguration] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [pickMode, setPickMode] = useState('random');
  const [manualGroupName, setManualGroupName] = useState(null);
  const [assigningTeam, setAssigningTeam] = useState(false);
  const [skipIndex, setSkipIndex] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please login again.');
        navigate('/login/tournament-admin');
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const tournamentRes = await axios.get(`${API_BASE_URL}/api/tournaments/${code}`, { headers });
      setTournament(tournamentRes.data.tournament);
      try {
        const pickingRes = await axios.get(`${API_BASE_URL}/api/grouping/${code}/picking-state`, { headers });
        if (pickingRes.data.success && pickingRes.data.initialized) {
          setPickingState(pickingRes.data);
          setGroups(pickingRes.data.groups || []);
          setShowConfiguration(false);
        } else {
          setPickingState(null);
          setGroups([]);
          setShowConfiguration(true);
        }
      } catch (err) {
        if (err.response?.status === 404 || err.response?.status === 403) {
          setPickingState(null);
          setGroups([]);
          setShowConfiguration(true);
        } else {
          throw err;
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied. You do not have permission to access this tournament.');
      } else if (error.response?.status === 404) {
        toast.error('Tournament not found.');
      } else {
        toast.error('Failed to load grouping data');
      }
      navigate(`/tournament/${code}/settings`);
    } finally {
      setLoading(false);
    }
  }, [code, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInitializeGroups = async (config) => {
    setInitializing(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/grouping/${code}/initialize-groups`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setShowConfiguration(false);
        await fetchData();
      }
    } catch (error) {
      console.error('Error initializing groups:', error);
      toast.error(error.response?.data?.message || 'Failed to initialize groups');
    } finally {
      setInitializing(false);
    }
  };

  const handleTeamPicked = async () => {
    setSkipIndex(0); // Reset skip when a pick completes
    await fetchData();
  };

  // Non-full groups sorted by teamCount asc, name asc (same as backend)
  const nonFullGroups = React.useMemo(() => {
    if (!pickingState?.groups || !pickingState?.initialized) return [];
    const cap = pickingState?.settings?.teamsPerGroup || 4;
    const list = pickingState.groups
      .filter(g => (g.teamCount ?? g.teams?.length ?? 0) < cap)
      .slice()
      .sort((a, b) => {
        const ca = a.teamCount ?? (a.teams?.length ?? 0);
        const cb = b.teamCount ?? (b.teams?.length ?? 0);
        if (ca !== cb) return ca - cb;
        return (a.name || '').localeCompare(b.name || '');
      });
    return list;
  }, [pickingState?.groups, pickingState?.settings?.teamsPerGroup, pickingState?.initialized]);

  const effectiveNextGroupName = React.useMemo(() => {
    const base = pickingState?.nextGroupName;
    if (nonFullGroups.length === 0) return base;
    const idx = skipIndex % nonFullGroups.length;
    return nonFullGroups[idx]?.name ?? base;
  }, [pickingState?.nextGroupName, nonFullGroups, skipIndex]);

  const handleSkip = () => {
    if (nonFullGroups.length <= 1) return;
    setSkipIndex(prev => (prev + 1) % nonFullGroups.length);
  };

  const handleLockGroups = async (lock) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = lock ? 'lock-groups' : 'unlock-groups';
      const response = await axios.post(
        `${API_BASE_URL}/api/grouping/${code}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        await fetchData();
      }
    } catch (error) {
      console.error(`Error ${lock ? 'locking' : 'unlocking'} groups:`, error);
      toast.error(error.response?.data?.message || `Failed to ${lock ? 'lock' : 'unlock'} groups`);
    }
  };

  const handleClearGroups = async () => {
    if (!window.confirm('Are you sure you want to clear all groups? This will remove all team assignments.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/grouping/${code}/clear-groups`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setShowConfiguration(true);
        await fetchData();
      }
    } catch (error) {
      console.error('Error clearing groups:', error);
      toast.error(error.response?.data?.message || 'Failed to clear groups');
    }
  };

  const handleSendToBroadcast = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/grouping/${code}/send-to-broadcast`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Error sending to broadcast:', error);
      toast.error(error.response?.data?.message || 'Failed to send to broadcast');
    }
  };

  const handleManualAssignTeam = async (teamId) => {
    if (!manualGroupName) return;
    setAssigningTeam(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/api/grouping/${code}/pick-team`,
        { teamId, groupName: manualGroupName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setManualGroupName(null);
        await fetchData();
      }
    } catch (error) {
      console.error('Error assigning team:', error);
      toast.error(error.response?.data?.message || 'Failed to assign team');
    } finally {
      setAssigningTeam(false);
    }
  };

  if (loading) {
    return (
      <div className="grouping-page-v2 grouping-loading-state">
        <div className="grouping-loading-spinner" />
        <p>Loading grouping data...</p>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="grouping-page-v2 grouping-error-state">
        <p>Tournament not found</p>
      </div>
    );
  }

  const isLocked = pickingState?.groups?.some(g => g.locked) || tournament.groupsLocked;
  const allTeamsAssigned = pickingState && pickingState.totalAvailable === 0;

  return (
    <div className="grouping-page-v2">
      {/* Toolbar */}
      <header className="grouping-toolbar">
        <div className="grouping-toolbar-brand">
          <span className="grouping-toolbar-icon">◉</span>
          <div>
            <h1 className="grouping-toolbar-title">Team Grouping</h1>
            <span className="grouping-toolbar-code">{tournament.name || code}</span>
          </div>
        </div>

        {pickingState?.initialized && (
          <div className="grouping-toolbar-stats">
            <div className="grouping-stat">
              <span className="grouping-stat-value">{pickingState.currentRound || 1}</span>
              <span className="grouping-stat-label">Round</span>
            </div>
            <div className="grouping-stat grouping-stat-progress">
              <span className="grouping-stat-value">
                {pickingState.totalAssigned || 0}
                <span className="grouping-stat-sep">/</span>
                {pickingState.totalAssigned + pickingState.totalAvailable}
              </span>
              <span className="grouping-stat-label">Teams</span>
            </div>
            {effectiveNextGroupName && (
              <div className="grouping-stat-group">
                <div className="grouping-stat grouping-stat-next">
                  <span className="grouping-stat-value">Group {effectiveNextGroupName}</span>
                  <span className="grouping-stat-label">Next</span>
                </div>
                {nonFullGroups.length > 1 && !allTeamsAssigned && (
                  <button
                    type="button"
                    className="grouping-skip-btn"
                    onClick={handleSkip}
                    title="Skip to next group"
                    aria-label="Skip to next group"
                  >
                    Skip
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="grouping-toolbar-actions">
          <a
            href={`/tournament/${code}/overview`}
            target="_blank"
            rel="noopener noreferrer"
            className="grouping-toolbar-link"
          >
            Overview
          </a>
          <a
            href={`/tournament/${code}/live`}
            target="_blank"
            rel="noopener noreferrer"
            className="grouping-toolbar-link"
          >
            Live
          </a>
          <a
            href={`/live/${code}`}
            target="_blank"
            rel="noopener noreferrer"
            className="grouping-toolbar-link"
          >
            Auction
          </a>
          {pickingState?.initialized && (
            <>
              <button
                type="button"
                className="grouping-toolbar-btn grouping-toolbar-btn-icon"
                onClick={() => setShowConfiguration(true)}
                disabled={isLocked}
                title="Settings"
                aria-label="Settings"
              >
                ⚙
              </button>
              <button
                type="button"
                className="grouping-toolbar-btn grouping-toolbar-btn-icon"
                onClick={handleClearGroups}
                disabled={isLocked}
                title="Clear groups"
                aria-label="Clear groups"
              >
                ⌫
              </button>
              <button
                type="button"
                className={`grouping-toolbar-btn ${isLocked ? 'grouping-toolbar-btn-locked' : 'grouping-toolbar-btn-unlock'}`}
                onClick={() => handleLockGroups(!isLocked)}
                title={isLocked ? 'Unlock groups' : 'Lock groups'}
              >
                {isLocked ? '🔒 Locked' : '🔓 Unlock'}
              </button>
              {allTeamsAssigned && (
                <button
                  type="button"
                  className="grouping-toolbar-btn grouping-toolbar-btn-primary"
                  onClick={handleSendToBroadcast}
                  title="Send to broadcast"
                >
                  Send to broadcast
                </button>
              )}
            </>
          )}
        </div>
      </header>

      {showConfiguration || !pickingState?.initialized ? (
        <main className="grouping-setup">
          <div className="grouping-setup-inner">
            <div className="grouping-setup-card">
              <GroupConfigurationPanel
                tournament={tournament}
                onInitialize={handleInitializeGroups}
                onCancel={() => pickingState?.initialized && setShowConfiguration(false)}
                initializing={initializing}
              />
            </div>
          </div>
        </main>
      ) : (
        <main className="grouping-workspace">
          {!allTeamsAssigned && (
            <aside className="grouping-sidebar">
              <div className="grouping-sidebar-card">
                <h2 className="grouping-sidebar-title">Assign teams</h2>
                <div className="grouping-mode-switch">
                  <button
                    type="button"
                    className={`grouping-mode-btn ${pickMode === 'random' ? 'is-active' : ''}`}
                    onClick={() => setPickMode('random')}
                  >
                    Random
                  </button>
                  <button
                    type="button"
                    className={`grouping-mode-btn ${pickMode === 'manual' ? 'is-active' : ''}`}
                    onClick={() => setPickMode('manual')}
                  >
                    Manual
                  </button>
                </div>
                {pickMode === 'random' && (
                  <TeamPickingInterface
                    tournamentCode={code}
                    pickingState={pickingState}
                    effectiveNextGroupName={effectiveNextGroupName}
                    onTeamPicked={handleTeamPicked}
                  />
                )}
                {pickMode === 'manual' && (
                  <div className="grouping-manual-hint">
                    Click <strong>Add team</strong> on a group card, then choose a team.
                  </div>
                )}
              </div>
            </aside>
          )}

          <section className={`grouping-main ${allTeamsAssigned ? 'grouping-main-full' : ''}`}>
            {allTeamsAssigned ? (
              <div className="grouping-done">
                <div className="grouping-done-icon">✓</div>
                <h2 className="grouping-done-title">All teams assigned</h2>
                <p className="grouping-done-text">
                  Lock groups if needed, then send the result to the live screen.
                </p>
                <button
                  type="button"
                  className="grouping-done-btn"
                  onClick={handleSendToBroadcast}
                >
                  Send to broadcast
                </button>
              </div>
            ) : (
              <>
                <div className="grouping-main-head">
                  <h2 className="grouping-main-title">Groups</h2>
                  <span className="grouping-main-subtitle">
                    {groups.length} groups · {pickingState?.totalAssigned || 0} / {pickingState?.totalAssigned + pickingState?.totalAvailable} teams
                  </span>
                </div>
                <div className="grouping-cards">
                  {groups.map((group) => {
                    const isFull = group.teamCount >= group.capacity;
                    const progress = group.capacity > 0 ? (group.teamCount / group.capacity) * 100 : 0;
                    const canAdd = !isFull && (pickingState?.availableTeams?.length > 0) && pickMode === 'manual';
                    return (
                      <div
                        key={group.name}
                        className={`grouping-card ${isFull ? 'is-full' : ''} ${group.locked ? 'is-locked' : ''}`}
                      >
                        <div className="grouping-card-header">
                          <span className="grouping-card-name">Group {group.name}</span>
                          <span className="grouping-card-count">
                            {group.teamCount} / {group.capacity}
                          </span>
                        </div>
                        <div className="grouping-card-progress-wrap">
                          <div className="grouping-card-progress-bar">
                            <div
                              className="grouping-card-progress-fill"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                        <div className="grouping-card-status">
                          {group.locked && <span className="grouping-card-status-pill is-locked">Locked</span>}
                          {isFull && !group.locked && <span className="grouping-card-status-pill is-full">Full</span>}
                          {!isFull && !group.locked && <span className="grouping-card-status-pill is-active">Active</span>}
                        </div>
                        <div className="grouping-card-teams">
                          {group.teams.length === 0 ? (
                            <span className="grouping-card-teams-empty">No teams yet</span>
                          ) : (
                            group.teams.map((team, idx) => (
                              <div key={team._id || idx} className="grouping-card-team">
                                {team.logo && (
                                  <img
                                    src={buildLogoUrl(team.logo)}
                                    alt=""
                                    className="grouping-card-team-logo"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                )}
                                <span className="grouping-card-team-name">{team.name}</span>
                              </div>
                            ))
                          )}
                        </div>
                        {pickMode === 'manual' && (
                          <button
                            type="button"
                            className="grouping-card-add"
                            disabled={!canAdd || assigningTeam}
                            onClick={() => setManualGroupName(group.name)}
                            title={isFull ? 'Group full' : !pickingState?.availableTeams?.length ? 'No teams left' : `Add team to Group ${group.name}`}
                          >
                            {assigningTeam && manualGroupName === group.name ? '…' : '+ Add team'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        </main>
      )}

      <TeamSelectionModal
        isOpen={!!manualGroupName}
        onClose={() => !assigningTeam && setManualGroupName(null)}
        teams={pickingState?.availableTeams || []}
        groupName={manualGroupName || ''}
        avoidSameCity={pickingState?.avoidSameCity || false}
        conflictingCities={manualGroupName ? (() => {
          const g = groups.find(gr => gr.name === manualGroupName);
          return new Set((g?.teams || []).map(t => t.city).filter(Boolean));
        })() : new Set()}
        onSelectTeam={handleManualAssignTeam}
      />
    </div>
  );
}

export default TournamentGrouping;
